import { type Href, Link, useRouter } from 'expo-router';
import { FormProvider, useForm } from 'react-hook-form';
import { useClient, useMutation, useQuery } from 'urql';
import Avatar from '@/components/Custom/Avatar';
import { MemberUpdate } from '@/components/Member/MemberUpdate';
import type { MemberUpdateFormValues } from '@/components/Member/MemberUpdate.types';
import Card from '@/components/UI/Card';
import PressableRow from '@/components/UI/PressableRow';
import Text from '@/components/UI/Text';
import Box from '@/components/Unistyles/Box';
import Icon from '@/components/Unistyles/Icon';
import { graphql } from '@/gql/graphql';
import useUserId from '@/hooks/useUserId';
import TeamPersonUpdateRoleInput from './components/TeamPersonUpdateRoleInput';
import { getTeamMemberSettingsActions } from './TeamPersonUpdate.utils';

const UpdateTeamPerson = graphql(/* GraphQL */ `
  mutation UpdateTeamPerson(
    $personId: String!
    $teamId: uuid!
    $role: role_enum!
    $memberType: member_type_enum!
    $selfMemberType: String!
    $shouldUpdateMemberType: Boolean!
    $shouldUpdateSelfMemberType: Boolean!
    $shouldUpdateRole: Boolean!
  ) {
    updateRole: update_person_team(
      where: { person_id: { _eq: $personId }, team_id: { _eq: $teamId } }
      _set: { role: $role }
    ) @include(if: $shouldUpdateRole) {
      affected_rows
    }
    updateMemberType: update_person_team(
      where: { person_id: { _eq: $personId }, team_id: { _eq: $teamId } }
      _set: { member_type: $memberType }
    ) @include(if: $shouldUpdateMemberType) {
      affected_rows
    }
    updateSelfMemberType: update_person_team_member_type_update(
      where: { person_id: { _eq: $personId }, team_id: { _eq: $teamId } }
      _set: { member_type: $selfMemberType }
    ) @include(if: $shouldUpdateSelfMemberType) {
      affected_rows
    }
  }
`);

const GetTeamPersonUpdate = graphql(/* GraphQL */ `
  query GetTeamPersonUpdateForm($personId: String!, $teamId: uuid!) {
    person_team_by_pk(person_id: $personId, team_id: $teamId) {
      person_id
      team_id
      role
      member_type
      person {
        id
        name
        avatar_id
        color
        is_managed
      }
      team {
        id
        title
        avatar_id
        home_primary_color
        permissions
        role
      }
    }
  }
`);

export default function TeamPersonUpdate({
  personId,
  teamId,
  removeHref,
}: {
  personId: string;
  teamId: string;
  removeHref?: Href;
}) {
  const client = useClient();
  const [, updateTeamPerson] = useMutation(UpdateTeamPerson);
  const { back } = useRouter();
  const userId = useUserId();

  const form = useForm<MemberUpdateFormValues>({
    defaultValues: async () => {
      const { data: formData } = await client.query(
        GetTeamPersonUpdate,
        {
          teamId,
          personId,
        },
        { requestPolicy: 'network-only' },
      );

      const teamPerson = formData?.person_team_by_pk;
      const memberType =
        teamPerson?.member_type === 'non_participant'
          ? 'non_participant'
          : 'participant';

      return {
        role: teamPerson?.role || 'player',
        memberType,
        color: teamPerson?.person.color,
        avatarId: teamPerson?.person.avatar_id,
        title: teamPerson?.person.name,
      };
    },
  });

  const {
    handleSubmit,
    setError,
    clearErrors,
    formState: { dirtyFields, errors, isSubmitting, isDirty, isLoading },
  } = form;

  const [{ data }] = useQuery({
    query: GetTeamPersonUpdate,
    variables: {
      teamId,
      personId,
    },
    requestPolicy: 'network-only',
  });

  const teamPerson = data?.person_team_by_pk;
  const team = teamPerson?.team;
  const isSelf = personId === userId;

  const reportSaveError = () =>
    setError('root.save', {
      message: 'Could not save all changes. Refresh and try again.',
    });

  const onSubmit = async (values: MemberUpdateFormValues) => {
    clearErrors('root.save');
    const shouldUpdateMemberType = Boolean(
      actions.editMemberType && dirtyFields.memberType,
    );
    const shouldUpdateRole = Boolean(actions.editRole && dirtyFields.role);

    if (!teamPerson || (!shouldUpdateMemberType && !shouldUpdateRole)) {
      return reportSaveError();
    }
    const { data: result, error } = await updateTeamPerson({
      teamId,
      personId,
      role: values.role,
      memberType: values.memberType,
      selfMemberType: values.memberType,
      shouldUpdateMemberType: shouldUpdateMemberType && !isSelf,
      shouldUpdateSelfMemberType: shouldUpdateMemberType && isSelf,
      shouldUpdateRole,
    });
    if (
      error ||
      (shouldUpdateRole && result?.updateRole?.affected_rows !== 1) ||
      (shouldUpdateMemberType &&
        !isSelf &&
        result?.updateMemberType?.affected_rows !== 1) ||
      (shouldUpdateMemberType &&
        isSelf &&
        result?.updateSelfMemberType?.affected_rows !== 1)
    ) {
      return reportSaveError();
    }
    back();
  };

  const actions = getTeamMemberSettingsActions({
    isManaged: teamPerson?.person.is_managed,
    isSelf,
    permissions: team?.permissions,
    role: teamPerson?.role,
  });

  const canEdit = actions.editMemberType || actions.editRole;
  const canDelete = Boolean(
    personId !== userId && team?.permissions?.delete_others,
  );

  return (
    <FormProvider {...form}>
      <MemberUpdate.Screen
        headerTitle={
          <>
            <Avatar
              size="md"
              name={team?.title}
              id={team?.avatar_id ?? ''}
              color={team?.home_primary_color}
            />
            <Box flex minWidth={0}>
              <Text size="xl" numberOfLines={1}>
                {team?.title ?? 'Team'}
              </Text>
              <Box direction="horizontal" align="center" space="sm">
                <Icon
                  name="people-outline"
                  size="sm"
                  variant="typographySecondary"
                />
                <Text size="xs" variant="typographySecondary" numberOfLines={1}>
                  Team
                </Text>
              </Box>
            </Box>
          </>
        }
        fetching={isLoading}
      >
        <MemberUpdate.Fields
          masthead={
            <MemberUpdate.Header>
              <MemberUpdate.RoleLabel
                isManaged={teamPerson?.person.is_managed}
              />
            </MemberUpdate.Header>
          }
        >
          {actions.editRole ? <TeamPersonUpdateRoleInput /> : null}
          {actions.editMemberType ? (
            <MemberUpdate.MemberTypeInput testID="TeamPersonUpdateMemberTypeSelect" />
          ) : null}
          {errors.root?.save?.message ? (
            <Text role="alert" variant="error">
              {errors.root.save.message}
            </Text>
          ) : null}
          {canDelete ? (
            <Card>
              <Link
                href={
                  removeHref ?? {
                    pathname: '/team/[teamId]/member/[personId]/edit/delete',
                    params: { teamId, personId },
                  }
                }
                asChild
              >
                <PressableRow>
                  <Icon name="trash-outline" variant="error" />
                  <Text variant="error">Remove from Team</Text>
                </PressableRow>
              </Link>
            </Card>
          ) : null}
        </MemberUpdate.Fields>
        {canEdit ? (
          <MemberUpdate.Save
            disabled={!isDirty || isSubmitting || isLoading}
            onSave={handleSubmit((values) =>
              onSubmit(values).catch(reportSaveError),
            )}
          />
        ) : null}
      </MemberUpdate.Screen>
    </FormProvider>
  );
}
